import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { notesApi, AgentNote } from '@/services/api/agentApi';

interface NotesState {
  notes: AgentNote[];
  activeNote: AgentNote | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
}

interface NotesActions {
  fetchNotes: (params?: Record<string, string>) => Promise<void>;
  createNote: (data: Partial<AgentNote>) => Promise<void>;
  updateNote: (noteId: string, data: Partial<AgentNote>) => Promise<void>;
  appendToNote: (noteId: string, text: string) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  searchNotes: (query: string) => Promise<void>;
  setActiveNote: (note: AgentNote | null) => void;
  setSearchQuery: (query: string) => void;
  loadNote: (noteId: string) => Promise<void>;
}

export const useNotesStore = create<NotesState & NotesActions>()(
  subscribeWithSelector(
    immer((set, get) => ({
      notes: [],
      activeNote: null,
      isLoading: false,
      error: null,
      searchQuery: '',

      fetchNotes: async (params) => {
        set((s) => { s.isLoading = true; s.error = null; });
        try {
          const result = await notesApi.list(params);
          set((s) => { s.notes = result.data || []; s.isLoading = false; });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; s.isLoading = false; });
        }
      },

      createNote: async (data) => {
        try {
          const result = await notesApi.create(data);
          await get().fetchNotes();
          if (result.data) {
            set((s) => { s.activeNote = result.data; });
          }
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      updateNote: async (noteId, data) => {
        try {
          const result = await notesApi.update(noteId, data);
          await get().fetchNotes();
          if (result.data && get().activeNote?.note_id === noteId) {
            set((s) => { s.activeNote = result.data; });
          }
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      appendToNote: async (noteId, text) => {
        try {
          const result = await notesApi.append(noteId, text);
          await get().fetchNotes();
          if (result.data && get().activeNote?.note_id === noteId) {
            set((s) => { s.activeNote = result.data; });
          }
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      deleteNote: async (noteId) => {
        try {
          await notesApi.remove(noteId);
          set((s) => {
            s.notes = s.notes.filter(n => n.note_id !== noteId);
            if (s.activeNote?.note_id === noteId) s.activeNote = null;
          });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },

      searchNotes: async (query) => {
        set((s) => { s.isLoading = true; s.searchQuery = query; });
        try {
          const result = await notesApi.search(query);
          set((s) => { s.notes = result.data || []; s.isLoading = false; });
        } catch (err) {
          set((s) => { s.error = (err as Error).message; s.isLoading = false; });
        }
      },

      setActiveNote: (note) => set((s) => { s.activeNote = note; }),

      setSearchQuery: (query) => set((s) => { s.searchQuery = query; }),

      loadNote: async (noteId) => {
        try {
          const result = await notesApi.get(noteId);
          if (result.data) {
            set((s) => { s.activeNote = result.data; });
          }
        } catch (err) {
          set((s) => { s.error = (err as Error).message; });
        }
      },
    }))
  )
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useNotesStore.setState({ notes: [], activeNote: null, isLoading: false, error: null, searchQuery: '' });
        }
      }
    );
  });
}
